// src/Dashboard/AdminDashboard.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, useClerk, useUser } from "@clerk/clerk-react";

import logo from "../assets/voplogo1.webp";

// Layout + nav
import Topbar from "./admin/layout/Topbar";
import Sidebar from "./admin/layout/Sidebar";
import MobileSidebar from "./admin/layout/MobileSidebar";
import Footer from "./admin/layout/Footer";
import ScopeFilters from "./admin/layout/ScopeFilters";
import { NAV } from "./admin/constants/nav";

// Hierarchy loaders
import {
  getConferences,
  getStations,
  getDistricts,
  getChurches,
  API_BASE,
} from "../services/api";

// Learners API (still used for Learners tab + topbar cache)
import { getAdminLearners } from "../services/adminApi";

// ✅ Reports API (backend-aligned)
import { getReportSummary, downloadCsv, downloadPdf } from "../services/reportsApi";

// ✅ Comms API
import {
  commsProviderStatus,
  commsSend,
  commsListBatches,
  commsListLogs,
  commsSearchRecipients,
} from "../services/communicationsApi";

// Sections
import OverviewSection from "./admin/sections/OverviewSection";
import LearnersSection from "./admin/sections/LearnersSection";
import ReportsSection from "./admin/sections/ReportsSection";
import InstructorsSection from "./admin/sections/InstructorsSection";
import AdminsSection from "./admin/sections/AdminsSection";
import AnnouncementsSection from "./admin/sections/AnnouncementsSection";
import CommsSection from "./admin/sections/CommsSection";
import HierarchySection from "./admin/sections/HierarchySection";
import CoursesSection from "./admin/sections/CoursesSection";
import RemindersSection from "./admin/sections/RemindersSection";
import SettingsSection from "./admin/sections/SettingsSection";

// Drawer
import LearnerDrawer from "./admin/components/LearnerDrawer";

// ✅ Audience Picker
import AudiencePicker from "./admin/components/AudiencePicker";

/* -------------------------------- Helpers -------------------------------- */

function safeId(x) {
  if (x === undefined || x === null) return "";
  return String(x);
}

function getIsActive(row) {
  if (typeof row?.is_active === "boolean") return row.is_active ? "1" : "0";
  if (row?.is_active === 1 || row?.is_active === "1") return "1";
  if (row?.isactive !== undefined) return String(row.isactive) === "1" ? "1" : "0";
  const s = String(row?.account_status || "").toLowerCase();
  if (s === "inactive") return "0";
  return "1";
}

function getConferenceId(x) {
  return safeId(x?.id ?? x?.conference_id ?? x?.conferenceId);
}
function getStationId(x) {
  return safeId(x?.id ?? x?.station_id ?? x?.stationId);
}
function getDistrictId(x) {
  return safeId(x?.id ?? x?.district_id ?? x?.districtId);
}
function getChurchId(x) {
  return safeId(x?.id ?? x?.church_id ?? x?.churchId);
}

// parent links (best-effort across schemas)
function getStationConferenceId(s) {
  return safeId(s?.conf_id ?? s?.conference_id ?? s?.conferenceId ?? s?.confId);
}
function getDistrictStationId(d) {
  return safeId(d?.station_id ?? d?.stationId);
}
function getChurchDistrictId(c) {
  return safeId(c?.district_id ?? c?.districtId);
}

function mapNameById(list, idGetter) {
  const m = new Map();
  (list || []).forEach((x) => {
    const id = safeId(idGetter(x));
    if (!id) return;
    m.set(id, x?.name || x?.label || id);
  });
  return m;
}

function makeInitials(nameOrEmail) {
  const s = String(nameOrEmail || "").trim();
  if (!s) return "A";
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    const one = parts[0].replace(/[^a-zA-Z0-9]/g, "");
    return (one.slice(0, 2) || "A").toUpperCase();
  }
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function authedGetJSON(token, path) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
  });

  const isJson = res.headers.get("content-type")?.includes("application/json");
  const body = isJson ? await res.json().catch(() => ({})) : {};
  if (!res.ok || body?.ok === false) {
    const msg = body?.error || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return body?.data ?? body;
}

// ✅ generic JSON writer (POST/PUT/DELETE)
async function authedWriteJSON(token, path, method, payload) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: payload === undefined ? undefined : JSON.stringify(payload),
  });

  const isJson = res.headers.get("content-type")?.includes("application/json");
  const body = isJson ? await res.json().catch(() => ({})) : {};
  if (!res.ok || body?.ok === false) {
    const msg = body?.error || body?.message || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return body?.data ?? body;
}

/* ------------------------------- Component -------------------------------- */

export default function AdminDashboard() {
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const { user } = useUser();

  // NAV / Layout
  const [activeSection, setActiveSection] = useState("overview");
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Theme (Topbar expects "light" | "dark")
  const [darkMode, setDarkMode] = useState(true);
  const theme = darkMode ? "dark" : "light";
  const setTheme = (updater) => {
    setDarkMode((prev) => {
      const current = prev ? "dark" : "light";
      const next = typeof updater === "function" ? updater(current) : updater;
      return next === "dark";
    });
  };

  // Topbar search + profile popovers
  const searchRef = useRef(null);
  const profileRef = useRef(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchScope, setSearchScope] = useState("all"); // all|learners|instructors|churches|courses|admins

  // Scope (church-level supported)
  const [level, setLevel] = useState("union"); // union|conference|station|district|church
  const [targetId, setTargetId] = useState("");

  // Browse chain (advanced scope selector)
  const [selectedConference, setSelectedConference] = useState("all");
  const [selectedStation, setSelectedStation] = useState("all");
  const [selectedDistrict, setSelectedDistrict] = useState("all");
  const [selectedChurch, setSelectedChurch] = useState("all");

  // Hierarchy data
  const [conferences, setConferences] = useState([]);
  const [stations, setStations] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [churches, setChurches] = useState([]);
  const [hierLoading, setHierLoading] = useState(false);
  const [hierError, setHierError] = useState("");

  // Global refresh signal to sections
  const [refreshNonce, setRefreshNonce] = useState(0);

  // Small caches for Topbar global search (learners + courses)
  const [searchLearnersLoading, setSearchLearnersLoading] = useState(false);
  const [searchLearnersError, setSearchLearnersError] = useState("");
  const [searchLearners, setSearchLearners] = useState([]);

  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState("");
  const [reportsSummary, setReportsSummary] = useState(null);

  // ✅ Reports filters (controlled by ReportsSection)
  const [repStatus, setRepStatus] = useState("active"); // active|inactive|all
  const [repGroupBy, setRepGroupBy] = useState("none"); // none|conference|district|church
  const [repQ, setRepQ] = useState("");
  const [repCourseSlug, setRepCourseSlug] = useState("");
  const [repIncludeCourses, setRepIncludeCourses] = useState(true);

  /* ---------------------------- Learners (page) ---------------------------- */

  const [learnersRows, setLearnersRows] = useState([]);
  const [learnersTotal, setLearnersTotal] = useState(null);
  const [learnersLoading, setLearnersLoading] = useState(false);
  const [learnersError, setLearnersError] = useState("");

  const [learnersQ, setLearnersQ] = useState("");
  const [learnersStatus, setLearnersStatus] = useState("all"); // all|active|inactive
  const [learnersPage, setLearnersPage] = useState(1);
  const learnersPageSize = 20;

  // Learner drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLearner, setDrawerLearner] = useState(null);
  const [drawerCourseSlug, setDrawerCourseSlug] = useState("");
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerError, setDrawerError] = useState("");
  const [drawerRows, setDrawerRows] = useState([]);

  /* ---------------------------- ✅ ANNOUNCEMENTS --------------------------- */

  const [annTitle, setAnnTitle] = useState("");
  const [annBody, setAnnBody] = useState("");
  const [annLoading, setAnnLoading] = useState(false);
  const [annError, setAnnError] = useState("");
  const [annList, setAnnList] = useState([]);

  /* ------------------------------- ✅ COMMS -------------------------------- */

  const [providerStatus, setProviderStatus] = useState({ email: null, sms: null });
  const [commsLoading, setCommsLoading] = useState(false);
  const [commsError, setCommsError] = useState("");
  const [commsLastResult, setCommsLastResult] = useState(null);

  // send form state
  const [commsMode, setCommsMode] = useState("filter"); // filter|users|recipients|audience
  const [commsUserIdsText, setCommsUserIdsText] = useState("");
  const [commsBatchLabel, setCommsBatchLabel] = useState("");
  const [commsSubject, setCommsSubject] = useState("");
  const [commsBody, setCommsBody] = useState("");

  // Individuals mode (search + select)
  const [recipientQuery, setRecipientQuery] = useState("");
  const [recipientSearching, setRecipientSearching] = useState(false);
  const [recipientResults, setRecipientResults] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);

  // AudiencePicker modal + state
  const [audienceOpen, setAudienceOpen] = useState(false);

  const [audienceType, setAudienceType] = useState("scope"); // scope|group|users
  const [audienceKey, setAudienceKey] = useState("");
  const [audienceCourseSlug, setAudienceCourseSlug] = useState("");

  const [audienceSearchQ, setAudienceSearchQ] = useState("");
  const [audienceRoleFilter, setAudienceRoleFilter] = useState("learner"); // learner|instructor|admin|all
  const [audienceStatusFilter, setAudienceStatusFilter] = useState("all"); // all|active|inactive
  const [audienceSearching, setAudienceSearching] = useState(false);
  const [audienceResults, setAudienceResults] = useState([]);
  const [audienceSelectedUsers, setAudienceSelectedUsers] = useState([]);

  // Batches
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [batchesQ, setBatchesQ] = useState("");
  const [batchesOffset, setBatchesOffset] = useState(0);

  // Logs
  const [logsLoading, setLogsLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsQ, setLogsQ] = useState("");
  const [logsStatus, setLogsStatus] = useState("");
  const [logsBatchId, setLogsBatchId] = useState("");
  const [logsOffset, setLogsOffset] = useState(0);

  /* ------------------------------ Derived maps ----------------------------- */

  const confNameById = useMemo(() => mapNameById(conferences, getConferenceId), [conferences]);
  const stationNameById = useMemo(() => mapNameById(stations, getStationId), [stations]);
  const districtNameById = useMemo(() => mapNameById(districts, getDistrictId), [districts]);
  const churchNameById = useMemo(() => mapNameById(churches, getChurchId), [churches]);

  // ✅ Courses for Announcements "graduates_by_course" (best-effort)
  const courses = useMemo(() => {
    const list = reportsSummary?.courses;
    return Array.isArray(list) ? list : [];
  }, [reportsSummary]);

  // ✅ SCOPE FIX: derive an effective target from the browse chain if targetId is empty
  const effectiveScope = useMemo(() => {
    const lvl = String(level || "union");
    if (lvl === "union") return { level: "union", targetId: "" };

    const t = String(targetId || "").trim();
    if (t) return { level: lvl, targetId: t };

    // fallback to chain selection
    if (lvl === "conference" && selectedConference && selectedConference !== "all") {
      return { level: lvl, targetId: String(selectedConference) };
    }
    if (lvl === "station" && selectedStation && selectedStation !== "all") {
      return { level: lvl, targetId: String(selectedStation) };
    }
    if (lvl === "district" && selectedDistrict && selectedDistrict !== "all") {
      return { level: lvl, targetId: String(selectedDistrict) };
    }
    if (lvl === "church" && selectedChurch && selectedChurch !== "all") {
      return { level: lvl, targetId: String(selectedChurch) };
    }

    return { level: lvl, targetId: "" };
  }, [level, targetId, selectedConference, selectedStation, selectedDistrict, selectedChurch]);

  const scopeKey = useMemo(
    () => `${effectiveScope.level}:${effectiveScope.targetId || "all"}`,
    [effectiveScope.level, effectiveScope.targetId]
  );

  // ✅ SCOPE FIX: if user picked via chain but targetId is empty, sync it so everything is consistent
  useEffect(() => {
    if (level === "union") {
      if (targetId) setTargetId("");
      return;
    }

    const t = String(targetId || "").trim();
    if (t) return;

    const derived = effectiveScope?.targetId ? String(effectiveScope.targetId) : "";
    if (derived) setTargetId(derived);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, targetId, effectiveScope.targetId]);

  const scopeLabel = useMemo(() => {
    const lvl = effectiveScope.level;
    const t = String(effectiveScope.targetId || "").trim();

    if (lvl === "union") return "Union (All)";
    if (!t) return `${lvl} (unspecified)`;

    if (lvl === "conference") return confNameById.get(t) || `Conference: ${t}`;
    if (lvl === "station") return stationNameById.get(t) || `Station: ${t}`;
    if (lvl === "district") return districtNameById.get(t) || `District: ${t}`;
    if (lvl === "church") return churchNameById.get(t) || `Church: ${t}`;
    return `${lvl}: ${t}`;
  }, [effectiveScope, confNameById, stationNameById, districtNameById, churchNameById]);

  const scopeChips = useMemo(() => {
    const lvl = effectiveScope.level;
    const t = String(effectiveScope.targetId || "").trim();

    if (lvl === "union") return ["Union"];
    if (!t) return [String(lvl).toUpperCase()];

    const chips = [];

    if (lvl === "conference") {
      chips.push(`Conference: ${confNameById.get(t) || t}`);
      return chips;
    }

    if (lvl === "station") {
      const st = (stations || []).find((x) => safeId(getStationId(x)) === t);
      const confId = st ? getStationConferenceId(st) : "";
      if (confId) chips.push(`Conference: ${confNameById.get(confId) || confId}`);
      chips.push(`Station: ${stationNameById.get(t) || t}`);
      return chips;
    }

    if (lvl === "district") {
      const dist = (districts || []).find((x) => safeId(getDistrictId(x)) === t);
      const stationId = dist ? getDistrictStationId(dist) : "";
      const st = stationId ? (stations || []).find((x) => safeId(getStationId(x)) === stationId) : null;
      const confId = st ? getStationConferenceId(st) : "";

      if (confId) chips.push(`Conference: ${confNameById.get(confId) || confId}`);
      if (stationId) chips.push(`Station: ${stationNameById.get(stationId) || stationId}`);
      chips.push(`District: ${districtNameById.get(t) || t}`);
      return chips;
    }

    if (lvl === "church") {
      const ch = (churches || []).find((x) => safeId(getChurchId(x)) === t);
      const districtId = ch ? getChurchDistrictId(ch) : "";
      const dist = districtId ? (districts || []).find((x) => safeId(getDistrictId(x)) === districtId) : null;
      const stationId = dist ? getDistrictStationId(dist) : "";
      const st = stationId ? (stations || []).find((x) => safeId(getStationId(x)) === stationId) : null;
      const confId = st ? getStationConferenceId(st) : "";

      if (confId) chips.push(`Conference: ${confNameById.get(confId) || confId}`);
      if (stationId) chips.push(`Station: ${stationNameById.get(stationId) || stationId}`);
      if (districtId) chips.push(`District: ${districtNameById.get(districtId) || districtId}`);
      chips.push(`Church: ${churchNameById.get(t) || t}`);
      return chips;
    }

    return [String(lvl).toUpperCase(), scopeLabel];
  }, [
    effectiveScope,
    scopeLabel,
    conferences,
    stations,
    districts,
    churches,
    confNameById,
    stationNameById,
    districtNameById,
    churchNameById,
  ]);

  const hierarchy = useMemo(
    () => ({ conferences, stations, districts, churches }),
    [conferences, stations, districts, churches]
  );

  const availableChurches = useMemo(() => {
    const lvl = effectiveScope.level;
    const t = String(effectiveScope.targetId || "").trim();

    if (!t || lvl === "union") return churches || [];

    if (lvl === "church") return (churches || []).filter((c) => String(getChurchId(c)) === t);
    if (lvl === "district") return (churches || []).filter((c) => String(getChurchDistrictId(c)) === t);

    if (lvl === "station") {
      const districtIds = (districts || [])
        .filter((d) => String(getDistrictStationId(d)) === t)
        .map((d) => String(getDistrictId(d)));
      const districtSet = new Set(districtIds);
      return (churches || []).filter((c) => districtSet.has(String(getChurchDistrictId(c))));
    }

    if (lvl === "conference") {
      const stationIds = (stations || [])
        .filter((s) => String(getStationConferenceId(s)) === t)
        .map((s) => String(getStationId(s)));
      const stationSet = new Set(stationIds);

      const districtIds = (districts || [])
        .filter((d) => stationSet.has(String(getDistrictStationId(d))))
        .map((d) => String(getDistrictId(d)));
      const districtSet = new Set(districtIds);

      return (churches || []).filter((c) => districtSet.has(String(getChurchDistrictId(c))));
    }

    return churches || [];
  }, [effectiveScope, churches, districts, stations]);

  /* ------------------------------- Loaders -------------------------------- */

  async function withToken(fn) {
    const token = await getToken();
    if (!token) throw new Error("Missing auth token");
    return fn(token);
  }

  // ✅ SCOPE FIX: always use effective scope (not raw level/targetId)
  function currentScopeParams() {
    return {
      level: effectiveScope.level,
      targetId: effectiveScope.level === "union" ? "" : String(effectiveScope.targetId || ""),
    };
  }

  async function loadHierarchy() {
    setHierLoading(true);
    setHierError("");
    try {
      const [cs, ss, ds, chs] = await Promise.all([
        getConferences(),
        getStations(),
        getDistricts(),
        getChurches(),
      ]);
      setConferences(cs || []);
      setStations(ss || []);
      setDistricts(ds || []);
      setChurches(chs || []);
    } catch (e) {
      setHierError(e?.message || "Failed to load hierarchy");
    } finally {
      setHierLoading(false);
    }
  }

  // ✅ SCOPE FIX: request guards (prevents stale responses overwriting current scope)
  const reqReportsRef = useRef(0);
  const reqLearnersRef = useRef(0);
  const reqSearchLearnersRef = useRef(0);
  const reqAnnouncementsRef = useRef(0);

  // ✅ Reports summary loader (uses filters + scope)
  async function loadReportsSummary() {
    const myReq = ++reqReportsRef.current;
    setReportsLoading(true);
    setReportsError("");

    const scope = currentScopeParams();

    try {
      await withToken(async (token) => {
        const params = {
          level: scope.level,
          targetId: scope.targetId,
          status: repStatus,
          groupBy: repGroupBy,
          q: repQ || "",
          courseSlug: repCourseSlug || "",
          includeCourses: repIncludeCourses ? "1" : "0",
        };

        const data = await getReportSummary(token, params);

        // ignore stale
        if (myReq !== reqReportsRef.current) return;
        setReportsSummary(data?.data ?? data);
      });
    } catch (e) {
      if (myReq !== reqReportsRef.current) return;
      setReportsError(e?.message || "Failed to load reports summary");
    } finally {
      if (myReq === reqReportsRef.current) setReportsLoading(false);
    }
  }

  async function onExportCsv() {
    await withToken(async (token) => {
      const scope = currentScopeParams();
      const params = {
        level: scope.level,
        targetId: scope.targetId,
        courseSlug: repCourseSlug || "",
      };
      const blob = await downloadCsv(token, params);
      downloadBlob(blob, `vop_report_${scope.level}_${Date.now()}.csv`);
    });
  }

  async function onExportPdf() {
    await withToken(async (token) => {
      const scope = currentScopeParams();
      const params = {
        level: scope.level,
        targetId: scope.targetId,
        status: repStatus,
        groupBy: repGroupBy,
        q: repQ || "",
        courseSlug: repCourseSlug || "",
        includeCourses: repIncludeCourses ? "1" : "0",
      };
      const blob = await downloadPdf(token, params);
      downloadBlob(blob, `vop_report_${scope.level}_${Date.now()}.pdf`);
    });
  }

  async function loadSearchLearners() {
    const myReq = ++reqSearchLearnersRef.current;
    setSearchLearnersLoading(true);
    setSearchLearnersError("");

    const scope = currentScopeParams();

    try {
      await withToken(async (token) => {
        const res = await getAdminLearners(token, {
          level: scope.level,
          targetId: scope.targetId,
          q: "",
          limit: 200,
          offset: 0,
        });

        if (myReq !== reqSearchLearnersRef.current) return;
        const rows = Array.isArray(res) ? res : res?.data || [];
        setSearchLearners(rows);
      });
    } catch (e) {
      if (myReq !== reqSearchLearnersRef.current) return;
      setSearchLearnersError(e?.message || "Failed to load learners search cache");
    } finally {
      if (myReq === reqSearchLearnersRef.current) setSearchLearnersLoading(false);
    }
  }

  // ✅ REAL Learners page loader
  async function loadLearnersPage() {
    const myReq = ++reqLearnersRef.current;
    setLearnersLoading(true);
    setLearnersError("");

    const scope = currentScopeParams();

    try {
      await withToken(async (token) => {
        const res = await getAdminLearners(token, {
          level: scope.level,
          targetId: scope.targetId,
          q: learnersQ,
          status: learnersStatus,
          limit: learnersPageSize,
          offset: (learnersPage - 1) * learnersPageSize,
        });

        if (myReq !== reqLearnersRef.current) return;

        const rows = Array.isArray(res) ? res : res?.data || [];
        const totalGuess =
          (Array.isArray(res)
            ? null
            : res?.total ?? res?.count ?? res?.pagination?.total ?? null) ?? null;

        setLearnersRows(rows);
        setLearnersTotal(typeof totalGuess === "number" ? totalGuess : null);
      });
    } catch (e) {
      if (myReq !== reqLearnersRef.current) return;
      setLearnersError(e?.message || "Failed to load learners");
      setLearnersRows([]);
      setLearnersTotal(null);
    } finally {
      if (myReq === reqLearnersRef.current) setLearnersLoading(false);
    }
  }

  /* ---------------------------- ✅ ANNOUNCEMENTS loaders ------------------- */

  async function loadAnnouncements() {
    const myReq = ++reqAnnouncementsRef.current;
    setAnnLoading(true);
    setAnnError("");

    const scope = currentScopeParams();
    const qs = new URLSearchParams();
    qs.set("level", scope.level);
    if (scope.level !== "union" && String(scope.targetId || "").trim()) {
      qs.set("targetId", String(scope.targetId).trim());
    }

    try {
      await withToken(async (token) => {
        const data = await authedGetJSON(token, `/api/admin/announcements?${qs.toString()}`);

        if (myReq !== reqAnnouncementsRef.current) return;

        const rows = Array.isArray(data) ? data : data?.rows || data?.data || [];
        setAnnList(Array.isArray(rows) ? rows : []);
      });
    } catch (e) {
      if (myReq !== reqAnnouncementsRef.current) return;
      setAnnError(e?.message || "Failed to load announcements");
      setAnnList([]);
    } finally {
      if (myReq === reqAnnouncementsRef.current) setAnnLoading(false);
    }
  }

  async function onCreateAnnouncement(payload) {
    setAnnLoading(true);
    setAnnError("");

    try {
      await withToken(async (token) => {
        // Backend contract (your previous api file): POST /api/admin/announcements
        await authedWriteJSON(token, `/api/admin/announcements`, "POST", payload);
      });

      // refresh list after create
      await loadAnnouncements();

      return { ok: true };
    } catch (e) {
      const msg = e?.message || "Failed to publish announcement";
      setAnnError(msg);
      return { ok: false, error: msg };
    } finally {
      setAnnLoading(false);
    }
  }

  async function onDeleteAnnouncement(id) {
    const rid = String(id || "").trim();
    if (!rid) return;

    setAnnLoading(true);
    setAnnError("");

    try {
      await withToken(async (token) => {
        // Most common REST shape:
        // DELETE /api/admin/announcements/:id
        await authedWriteJSON(token, `/api/admin/announcements/${encodeURIComponent(rid)}`, "DELETE");
      });

      // optimistic update + reload (safe)
      setAnnList((prev) => (prev || []).filter((x) => String(x?.id || "") !== rid));
      await loadAnnouncements();
    } catch (e) {
      setAnnError(e?.message || "Failed to delete announcement");
    } finally {
      setAnnLoading(false);
    }
  }

  /* ------------------------------- ✅ COMMS loaders ------------------------ */

  async function loadCommsMeta() {
    setCommsLoading(true);
    setCommsError("");
    try {
      await withToken(async (token) => {
        const data = await commsProviderStatus(token);
        setProviderStatus(data || { email: null, sms: null });
      });
    } catch (e) {
      setCommsError(e?.message || "Failed to load provider status");
    } finally {
      setCommsLoading(false);
    }
  }

  async function loadCommsBatches(channel) {
    setBatchesLoading(true);
    setCommsError("");
    try {
      await withToken(async (token) => {
        const data = await commsListBatches(token, {
          channel,
          q: batchesQ || "",
          limit: 25,
          offset: batchesOffset || 0,
        });
        setBatches(Array.isArray(data) ? data : []);
      });
    } catch (e) {
      setCommsError(e?.message || "Failed to load batches");
      setBatches([]);
    } finally {
      setBatchesLoading(false);
    }
  }

  async function loadCommsLogs(channel) {
    setLogsLoading(true);
    setCommsError("");
    try {
      await withToken(async (token) => {
        const data = await commsListLogs(token, {
          channel,
          q: logsQ || "",
          status: logsStatus || "",
          batchId: logsBatchId || "",
          limit: 50,
          offset: logsOffset || 0,
        });
        setLogs(Array.isArray(data) ? data : []);
      });
    } catch (e) {
      setCommsError(e?.message || "Failed to load logs");
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }

  // ✅ Recipient search (backend expects `role`, not `roles`)
  async function searchRecipients(q, { roleFilter = "all", statusFilter = "all" } = {}) {
    const trimmed = String(q || "").trim();
    if (!trimmed) return [];

    const roleParam =
      roleFilter === "all" ? "all" : roleFilter === "admin" ? "admin" : roleFilter; // learner|instructor|admin
    const statusParam = statusFilter || "all";

    const rows = await withToken(async (token) => {
      const data = await commsSearchRecipients(token, {
        q: trimmed,
        role: roleParam,
        status: statusParam,
        limit: 50,
        offset: 0,
      });

      return Array.isArray(data) ? data : [];
    });

    return rows;
  }

  // ✅ Used by AnnouncementsSection (individual targeting)
  async function searchAudienceUsers({ q, role, status } = {}) {
    return searchRecipients(q, {
      roleFilter: role || "all",
      statusFilter: status || "all",
    });
  }

  // Individuals search (debounced)
  useEffect(() => {
    let alive = true;
    const q = String(recipientQuery || "").trim();
    if (commsMode !== "recipients") return undefined;

    if (q.length < 2) {
      setRecipientResults([]);
      return undefined;
    }

    const t = setTimeout(async () => {
      setRecipientSearching(true);
      try {
        const rows = await searchRecipients(q, { roleFilter: "all", statusFilter: "all" });
        if (!alive) return;
        setRecipientResults(rows);
      } catch (e) {
        if (!alive) return;
        setRecipientResults([]);
        setCommsError(e?.message || "Recipient search failed");
      } finally {
        if (alive) setRecipientSearching(false);
      }
    }, 300);

    return () => {
      alive = false;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipientQuery, commsMode, scopeKey]);

  function addRecipient(p) {
    const id = String(p?.id || "");
    if (!id) return;
    setSelectedRecipients((prev) => {
      const exists = (prev || []).some((x) => String(x?.id || "") === id);
      if (exists) return prev;
      return [...(prev || []), p];
    });
  }

  function removeRecipient(pOrId) {
    const id = typeof pOrId === "string" ? pOrId : String(pOrId?.id || "");
    if (!id) return;
    setSelectedRecipients((prev) => (prev || []).filter((x) => String(x?.id || "") !== id));
  }

  function buildAudienceSelection() {
    return {
      audienceType,
      audienceKey,
      courseSlug: audienceCourseSlug,
      searchQ: audienceSearchQ,
      roleFilter: audienceRoleFilter,
      statusFilter: audienceStatusFilter,
      selectedUsers: audienceSelectedUsers,
    };
  }

  async function onAudienceSearch() {
    const q = String(audienceSearchQ || "").trim();
    if (!q) return;

    setAudienceSearching(true);
    setCommsError("");
    try {
      const rows = await searchRecipients(q, {
        roleFilter: audienceRoleFilter,
        statusFilter: audienceStatusFilter,
      });
      setAudienceResults(rows);
    } catch (e) {
      setAudienceResults([]);
      setCommsError(e?.message || "Audience search failed");
    } finally {
      setAudienceSearching(false);
    }
  }

  function clearAudience() {
    setAudienceType("scope");
    setAudienceKey("");
    setAudienceCourseSlug("");
    setAudienceSearchQ("");
    setAudienceRoleFilter("learner");
    setAudienceStatusFilter("all");
    setAudienceResults([]);
    setAudienceSelectedUsers([]);
  }

  async function onSendComms(channel, payload) {
    setCommsLoading(true);
    setCommsError("");
    try {
      await withToken(async (token) => {
        const scope = currentScopeParams();

        const out = {
          channel,
          batchLabel: payload?.batchLabel,
          subject: payload?.subject,
          body: payload?.body,
          scope,
        };

        if (Array.isArray(payload?.userIds) && payload.userIds.length) {
          out.userIds = payload.userIds;
        }

        if (payload?.audience) {
          out.audience = payload.audience;
        }

        const data = await commsSend(token, out);
        setCommsLastResult(data || null);

        await Promise.allSettled([loadCommsBatches(channel), loadCommsLogs(channel)]);
      });
    } catch (e) {
      setCommsError(e?.message || "Failed to send");
    } finally {
      setCommsLoading(false);
    }
  }

  async function refreshAll() {
    setRefreshNonce((n) => n + 1);
    await Promise.allSettled([
      loadHierarchy(),
      loadReportsSummary(),
      loadSearchLearners(),
      loadLearnersPage(),
      loadCommsMeta(),
      loadAnnouncements(),
    ]);
  }

  /* ------------------------------ Drawer logic ----------------------------- */

  const learnerChurchNameFallback = (l) => {
    const cid = safeId(l?.church_id ?? l?.churchId);
    if (!cid) return "";
    return churchNameById.get(cid) || cid;
  };

  const drawerChurchLabel = useMemo(() => {
    if (!drawerLearner) return "—";
    const cid = safeId(drawerLearner?.church_id ?? drawerLearner?.churchId);
    return (cid && (churchNameById.get(cid) || cid)) || learnerChurchNameFallback(drawerLearner) || "—";
  }, [drawerLearner, churchNameById]);

  function pickLearnerId(l) {
    return safeId(l?.id ?? l?.user_id ?? l?.uid ?? l?.clerk_user_id ?? l?.clerkUserId);
  }

  async function loadLearnerRollup(learnerRow) {
    const uid = pickLearnerId(learnerRow);
    if (!uid) {
      setDrawerRows([]);
      setDrawerError("Missing learner id (id/user_id).");
      return;
    }

    setDrawerLoading(true);
    setDrawerError("");

    try {
      await withToken(async (token) => {
        const cs = String(drawerCourseSlug || "").trim();

        // ✅ This matches your backend routes exactly:
        // GET /api/admin/reports/users/:userId/course-rollup?courseSlug=
        const path =
          `/api/admin/reports/users/${encodeURIComponent(uid)}/course-rollup` +
          (cs ? `?courseSlug=${encodeURIComponent(cs)}` : "");

        const data = await authedGetJSON(token, path);

        // Controller returns: { ok:true, data:{ rows:[...] } }
        const rows = Array.isArray(data)
          ? data
          : data?.rows || data?.data?.rows || data?.data || [];

        setDrawerRows(Array.isArray(rows) ? rows : []);
      });
    } catch (e) {
      setDrawerRows([]);
      const msg = e?.message || "Failed to load learner rollup.";
      setDrawerError(msg);
    } finally {
      setDrawerLoading(false);
    }
  }

  function openLearnerDrawer(row) {
    setDrawerLearner(row);
    setDrawerOpen(true);
    setDrawerRows([]);
    setDrawerError("");
    loadLearnerRollup(row);
  }

  function closeLearnerDrawer() {
    setDrawerOpen(false);
    setDrawerLearner(null);
    setDrawerRows([]);
    setDrawerError("");
    setDrawerCourseSlug("");
  }

  /* -------------------------------- Effects ------------------------------- */

  useEffect(() => {
    document.documentElement.classList.toggle("dark", !!darkMode);
  }, [darkMode]);

  useEffect(() => {
    loadHierarchy();
    loadCommsMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ SCOPE FIX: scope change refresh should depend on scopeKey (effective scope), not raw values
  useEffect(() => {
    setRefreshNonce((n) => n + 1);
    setLearnersPage(1);

    loadReportsSummary();
    loadSearchLearners();
    loadLearnersPage();

    // announcements should follow scope
    if (activeSection === "announcements") {
      loadAnnouncements();
    }

    setBatchesOffset(0);
    setLogsOffset(0);
    if (activeSection === "sms" || activeSection === "email") {
      const channel = activeSection === "sms" ? "sms" : "email";
      loadCommsBatches(channel);
      loadCommsLogs(channel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  // reports filters change refresh (light debounce for q)
  useEffect(() => {
    let alive = true;
    const t = setTimeout(() => {
      if (!alive) return;
      loadReportsSummary();
    }, 350);
    return () => {
      alive = false;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repStatus, repGroupBy, repQ, repCourseSlug, repIncludeCourses, scopeKey]);

  useEffect(() => {
    loadLearnersPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learnersPage, learnersQ, learnersStatus, scopeKey]);

  useEffect(() => {
    function onDocMouseDown(e) {
      const t = e.target;
      if (searchRef.current && !searchRef.current.contains(t)) setSearchOpen(false);
      if (profileRef.current && !profileRef.current.contains(t)) setProfileOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  useEffect(() => {
    function onKeyDown(e) {
      const tag = (e.target?.tagName || "").toLowerCase();
      const typing = tag === "input" || tag === "textarea" || e.target?.isContentEditable;

      if (!typing && e.key === "/") {
        e.preventDefault();
        setSearchOpen(true);
        setProfileOpen(false);
      }
      if (e.key === "Escape") {
        setSearchOpen(false);
        setProfileOpen(false);
        setMobileNavOpen(false);
        setAudienceOpen(false);
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  /* ------------------------------- NAV handler ----------------------------- */

  function handleSelectSection(id) {
    setActiveSection(id);
    setMobileNavOpen(false);
    setSearchOpen(false);
    setProfileOpen(false);

    if (id === "sms" || id === "email") {
      const channel = id === "sms" ? "sms" : "email";
      loadCommsMeta();
      loadCommsBatches(channel);
      loadCommsLogs(channel);
    }

    // ✅ load announcements when tab opened
    if (id === "announcements") {
      loadAnnouncements();
    }
  }

  /* ------------------------- Topbar search results ------------------------- */

  const normalizedQuery = useMemo(
    () => String(searchQuery || "").trim().toLowerCase(),
    [searchQuery]
  );

  const learnerSearchResults = useMemo(() => {
    if (!normalizedQuery) return [];
    const q = normalizedQuery;
    return (searchLearners || [])
      .filter((l) => {
        const name = String(l.full_name || l.fullName || "").toLowerCase();
        const email = String(l.email || "").toLowerCase();
        const church = String(learnerChurchNameFallback(l) || "").toLowerCase();
        return name.includes(q) || email.includes(q) || church.includes(q);
      })
      .slice(0, 8);
  }, [searchLearners, normalizedQuery]); // churchNameById not needed

  const churchSearchResults = useMemo(() => {
    if (!normalizedQuery) return [];
    const q = normalizedQuery;
    return (churches || [])
      .filter((c) => String(c?.name || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [churches, normalizedQuery]);

  const courseSearchResults = useMemo(() => {
    if (!normalizedQuery) return [];
    const q = normalizedQuery;
    const list = reportsSummary?.courses || [];
    return (Array.isArray(list) ? list : [])
      .filter((c) => {
        const slug = String(c?.slug || c?.course_slug || c?.courseSlug || "").toLowerCase();
        const name = String(c?.title || c?.name || "").toLowerCase();
        return slug.includes(q) || name.includes(q);
      })
      .slice(0, 8);
  }, [reportsSummary, normalizedQuery]);

  const instructorSearchResults = useMemo(() => [], []);
  const adminSearchResults = useMemo(() => [], []);

  /* ------------------------------- UI bits -------------------------------- */

  const navItems = NAV;

  const meLabel =
    user?.fullName || user?.primaryEmailAddress?.emailAddress || user?.username || "Admin";
  const initials = makeInitials(
    user?.fullName || user?.primaryEmailAddress?.emailAddress || "Admin"
  );

  const spinning =
    hierLoading ||
    reportsLoading ||
    searchLearnersLoading ||
    learnersLoading ||
    commsLoading ||
    annLoading;

  const topError =
    hierError ||
    reportsError ||
    searchLearnersError ||
    learnersError ||
    commsError ||
    annError ||
    "";

  /* -------------------------------- Render -------------------------------- */

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      <Topbar
        activeSection={activeSection}
        scopeChips={scopeChips}
        error={topError}
        onOpenMobileSidebar={() => setMobileNavOpen(true)}
        onRefresh={refreshAll}
        spinning={spinning}
        searchRef={searchRef}
        searchOpen={searchOpen}
        setSearchOpen={setSearchOpen}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        searchScope={searchScope}
        setSearchScope={setSearchScope}
        normalizedQuery={normalizedQuery}
        learnerSearchResults={learnerSearchResults}
        instructorSearchResults={instructorSearchResults}
        churchSearchResults={churchSearchResults}
        churchNameById={churchNameById}
        getChurchId={getChurchId}
        learnerChurchNameFallback={learnerChurchNameFallback}
        setActiveSection={handleSelectSection}
        theme={theme}
        setTheme={setTheme}
        profileRef={profileRef}
        profileOpen={profileOpen}
        setProfileOpen={setProfileOpen}
        displayName={meLabel}
        initials={initials}
        signOut={signOut}
        courseSearchResults={courseSearchResults}
        adminSearchResults={adminSearchResults}
      />

      <MobileSidebar
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        logoSrc={logo}
        activeSection={activeSection}
        onSelectSection={handleSelectSection}
        navItems={navItems}
      />

      <div className="min-h-[calc(100vh-64px)] flex">
        <Sidebar
          logoSrc={logo}
          sidebarExpanded={sidebarExpanded}
          activeSection={activeSection}
          setActiveSection={handleSelectSection}
          navItems={navItems}
        />

        <main className="flex-1">
          <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
            <ScopeFilters
              loading={hierLoading}
              level={level}
              setLevel={setLevel}
              targetId={targetId}
              setTargetId={setTargetId}
              selectedConference={selectedConference}
              selectedStation={selectedStation}
              selectedDistrict={selectedDistrict}
              selectedChurch={selectedChurch}
              setSelectedConference={setSelectedConference}
              setSelectedStation={setSelectedStation}
              setSelectedDistrict={setSelectedDistrict}
              setSelectedChurch={setSelectedChurch}
              conferences={conferences}
              stations={stations}
              districts={districts}
              churches={churches}
              getIsActive={getIsActive}
              getConferenceId={getConferenceId}
              getStationId={getStationId}
              getDistrictId={getDistrictId}
              getChurchId={getChurchId}
              getStationConferenceId={getStationConferenceId}
              getDistrictStationId={getDistrictStationId}
              getChurchDistrictId={getChurchDistrictId}
              onRefreshHierarchy={loadHierarchy}
              scopeLabel={scopeLabel}
            />

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setSidebarExpanded((v) => !v)}
                className="hidden md:inline-flex px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
                title="Toggle sidebar width"
              >
                {sidebarExpanded ? "Collapse sidebar" : "Expand sidebar"}
              </button>
            </div>

            {activeSection === "overview" && (
              <OverviewSection
                getToken={getToken}
                level={effectiveScope.level}
                targetId={effectiveScope.level === "union" ? "" : effectiveScope.targetId}
                scopeLabel={scopeLabel}
                scopeChips={scopeChips}
                hierarchy={hierarchy}
                reportsSummary={reportsSummary}
                reportsLoading={reportsLoading}
                reportsError={reportsError}
                onReloadReports={loadReportsSummary}
                refreshNonce={refreshNonce}
                setActiveSection={handleSelectSection}
              />
            )}

            {activeSection === "reports" && (
              <ReportsSection
                reportsLoading={reportsLoading}
                reportsError={reportsError}
                reportsSummary={reportsSummary}
                scopeChips={scopeChips}
                onRefresh={loadReportsSummary}
                onExportCsv={onExportCsv}
                onExportPdf={onExportPdf}
                repStatus={repStatus}
                setRepStatus={setRepStatus}
                repGroupBy={repGroupBy}
                setRepGroupBy={setRepGroupBy}
                repQ={repQ}
                setRepQ={setRepQ}
                repCourseSlug={repCourseSlug}
                setRepCourseSlug={setRepCourseSlug}
                repIncludeCourses={repIncludeCourses}
                setRepIncludeCourses={setRepIncludeCourses}
              />
            )}

            {activeSection === "people-learners" && (
              <LearnersSection
                learners={learnersRows}
                total={learnersTotal}
                loading={learnersLoading}
                error={learnersError}
                q={learnersQ}
                onQChange={(v) => {
                  setLearnersPage(1);
                  setLearnersQ(v);
                }}
                status={learnersStatus}
                onStatusChange={(v) => {
                  setLearnersPage(1);
                  setLearnersStatus(v);
                }}
                onRefresh={loadLearnersPage}
                onOpenLearner={openLearnerDrawer}
                churchNameById={churchNameById}
                learnerChurchNameFallback={learnerChurchNameFallback}
              />
            )}

            {activeSection === "people-instructors" && (
              <InstructorsSection
                getToken={getToken}
                hierarchy={hierarchy}
                showScopeFilters
                refreshNonce={refreshNonce}
              />
            )}

            {activeSection === "people-admins" && (
              <AdminsSection
                getToken={getToken}
                hierarchy={{ conferences, districts }}
                refreshNonce={refreshNonce}
              />
            )}

            {activeSection === "announcements" && (
              <AnnouncementsSection
                /* Scope (from global selector) */
                level={effectiveScope.level}
                targetId={effectiveScope.level === "union" ? "" : effectiveScope.targetId}
                scopeLabel={scopeLabel}
                scopeChips={scopeChips || []}

                /* Create form (controlled from AdminDashboard state) */
                annTitle={annTitle}
                setAnnTitle={setAnnTitle}
                annBody={annBody}
                setAnnBody={setAnnBody}
                annLoading={annLoading}
                onCreateAnnouncement={onCreateAnnouncement}

                /* List */
                annList={annList}
                loadAnnouncements={loadAnnouncements}
                onDeleteAnnouncement={onDeleteAnnouncement}

                /* Optional (audience search + graduates group) */
                searchAudienceUsers={searchAudienceUsers}
                courses={courses || []}
              />
            )}

            {(activeSection === "sms" || activeSection === "email") && (
              <CommsSection
                activeSection={activeSection}
                scopeChips={scopeChips}
                currentScopeParams={currentScopeParams}
                providerStatus={providerStatus}
                commsLoading={commsLoading}
                commsError={commsError}
                loadCommsMeta={loadCommsMeta}
                commsMode={commsMode}
                setCommsMode={setCommsMode}
                commsUserIdsText={commsUserIdsText}
                setCommsUserIdsText={setCommsUserIdsText}
                commsBatchLabel={commsBatchLabel}
                setCommsBatchLabel={setCommsBatchLabel}
                commsSubject={commsSubject}
                setCommsSubject={setCommsSubject}
                commsBody={commsBody}
                setCommsBody={setCommsBody}
                onSendComms={onSendComms}
                commsLastResult={commsLastResult}
                setCommsLastResult={setCommsLastResult}
                recipientQuery={recipientQuery}
                setRecipientQuery={setRecipientQuery}
                recipientResults={recipientResults}
                selectedRecipients={selectedRecipients}
                onAddRecipient={addRecipient}
                onRemoveRecipient={removeRecipient}
                maxRecipients={500}
                audienceSelection={buildAudienceSelection()}
                onOpenAudiencePicker={() => setAudienceOpen(true)}
                onClearAudience={clearAudience}
                batchesLoading={batchesLoading}
                batches={batches}
                batchesQ={batchesQ}
                setBatchesQ={setBatchesQ}
                batchesOffset={batchesOffset}
                setBatchesOffset={setBatchesOffset}
                loadCommsBatches={loadCommsBatches}
                logsLoading={logsLoading}
                logs={logs}
                logsQ={logsQ}
                setLogsQ={setLogsQ}
                logsStatus={logsStatus}
                setLogsStatus={setLogsStatus}
                logsBatchId={logsBatchId}
                setLogsBatchId={setLogsBatchId}
                logsOffset={logsOffset}
                setLogsOffset={setLogsOffset}
                loadCommsLogs={loadCommsLogs}
              />
            )}

            {activeSection === "hierarchy" && (
              <HierarchySection
                conferences={conferences}
                stations={stations}
                districts={districts}
                churches={churches}
                availableChurches={availableChurches}
                getIsActive={getIsActive}
                getConferenceId={getConferenceId}
                getStationId={getStationId}
                getDistrictId={getDistrictId}
                getChurchId={getChurchId}
                loadingHierarchy={hierLoading}
                onReloadHierarchy={loadHierarchy}
              />
            )}

            {activeSection === "courses" && (
              <CoursesSection
                level={effectiveScope.level}
                targetId={effectiveScope.level === "union" ? "" : effectiveScope.targetId}
                scopeLabel={scopeLabel}
                hierarchy={hierarchy}
                scopeChips={scopeChips}
                reportsSummary={reportsSummary}
                reportsLoading={reportsLoading}
                reportsError={reportsError}
                onReloadReports={loadReportsSummary}
                refreshNonce={refreshNonce}
              />
            )}

            {activeSection === "reminders" && (
              <RemindersSection
                getToken={getToken}
                level={effectiveScope.level}
                targetId={effectiveScope.level === "union" ? "" : effectiveScope.targetId}
                scopeLabel={scopeLabel}
                hierarchy={hierarchy}
                refreshNonce={refreshNonce}
              />
            )}

            {activeSection === "settings" && (
              <SettingsSection
                getToken={getToken}
                level={effectiveScope.level}
                targetId={effectiveScope.level === "union" ? "" : effectiveScope.targetId}
                scopeLabel={scopeLabel}
                hierarchy={hierarchy}
                refreshNonce={refreshNonce}
              />
            )}

            <Footer appName="VOP-LMS" scopeChips={scopeChips} />

            <div className="pt-2 text-xs text-slate-500">
              <Link to="/" className="underline">
                VOP
              </Link>
            </div>
          </div>
        </main>
      </div>

      {/* ✅ Audience Picker Modal */}
      {audienceOpen ? (
        <div
          className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setAudienceOpen(false);
          }}
        >
          <div className="w-full max-w-3xl rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xl">
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold">Audience Picker</div>
                <div className="text-xs text-slate-500">
                  Scope is selected above; audience is evaluated inside scope.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setAudienceOpen(false)}
                className="px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                Close
              </button>
            </div>

            <div className="p-4">
              <AudiencePicker
                audienceType={audienceType}
                setAudienceType={setAudienceType}
                audienceKey={audienceKey}
                setAudienceKey={setAudienceKey}
                courseSlug={audienceCourseSlug}
                setCourseSlug={setAudienceCourseSlug}
                searchQ={audienceSearchQ}
                setSearchQ={setAudienceSearchQ}
                roleFilter={audienceRoleFilter}
                setRoleFilter={setAudienceRoleFilter}
                statusFilter={audienceStatusFilter}
                setStatusFilter={setAudienceStatusFilter}
                searching={audienceSearching}
                results={audienceResults}
                onSearch={onAudienceSearch}
                selectedUsers={audienceSelectedUsers}
                setSelectedUsers={setAudienceSelectedUsers}
              />

              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    clearAudience();
                    setAudienceOpen(false);
                  }}
                  className="px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => setAudienceOpen(false)}
                  className="px-4 py-2 rounded-xl bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 text-sm font-semibold"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* ✅ Learner Drawer */}
      <LearnerDrawer
        open={drawerOpen}
        onClose={closeLearnerDrawer}
        learner={drawerLearner}
        churchLabel={drawerChurchLabel}
        loading={drawerLoading}
        error={drawerError}
        rows={drawerRows}
        courseSlug={drawerCourseSlug}
        onCourseSlugChange={setDrawerCourseSlug}
        onApplyCourseSlug={() => {
          if (drawerLearner) loadLearnerRollup(drawerLearner);
        }}
      />
    </div>
  );
}
